import { ShowBtn } from "@/components/Show";
import { CreateLoaderInput } from "@/components/Loaders";
import { getShowTimeEntry, updateTimeEntryLocation } from "@/Api/timeEntries";
import { TimeEntryTypeBadge } from "@/components/TimeEntryTypeBadge";
import { useEffect, useRef, useState } from "react";
import { GoogleMap, loadGoogleMapsScript } from "@/components/GoogleMap";
import { resolveApiKey } from "@/App/Geofences/shared/GeofenceMap";
import { Smartphone, MapPin, AlertTriangle, Pencil, MapPinned } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { usePermissions } from "@/hooks/usePermissions";
import { useToastNavigate } from "@/utils/toastNavigate";
import LogoFavicon from "../../../../public/images/logo-favicon.png";

// Mesmo mapa (Google Maps) usado no resto do sistema; a chave vem da config do tenant.
const useGoogleMapsReady = () => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        await loadGoogleMapsScript(await resolveApiKey());
        setLoaded(true);
      } catch (error) {
        console.error("Error loading Google Maps:", error);
      }
    })();
  }, []);

  return loaded;
};

const MapLoading = () => (
  <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
    Carregando mapa...
  </div>
);

type LatLng = { lat: number; lng: number };

// Mapa com marcador arrastável; clicar no mapa também move o marcador.
const EditableMap = ({
  position,
  onChange,
}: {
  position: LatLng;
  onChange: (position: LatLng) => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const markerRef = useRef<any>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const google = (window as any).google;
    if (!ref.current || !google?.maps) return;

    const map = new google.maps.Map(ref.current, {
      center: position,
      zoom: 17,
      streetViewControl: false,
      fullscreenControl: true,
    });
    const marker = new google.maps.Marker({
      position,
      map,
      draggable: true,
      title: "Arraste para o endereço correto",
    });
    markerRef.current = marker;

    const emit = (latLng: any) =>
      onChangeRef.current({ lat: latLng.lat(), lng: latLng.lng() });

    marker.addListener("dragend", (e: any) => emit(e.latLng));
    map.addListener("click", (e: any) => {
      marker.setPosition(e.latLng);
      emit(e.latLng);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div ref={ref} className="h-full w-full" />;
};

const TimeEntryMap = ({
  position,
  address,
  editing,
  onChange,
}: {
  position: LatLng;
  address?: string;
  editing: boolean;
  onChange: (position: LatLng) => void;
}) => {
  const loaded = useGoogleMapsReady();

  if (!loaded) return <MapLoading />;

  // `key` remonta o mapa ao entrar/sair da edição (marcador volta ao ponto salvo).
  return editing ? (
    <EditableMap key="edit" position={position} onChange={onChange} />
  ) : (
    <GoogleMap
      key={`view-${position.lat}-${position.lng}`}
      latitude={position.lat}
      longitude={position.lng}
      address={address}
      className="!h-full"
    />
  );
};

const TimeEntryDetails = ({
  responseApi,
  startEditing = false,
  onSaved,
}: {
  responseApi: any;
  startEditing?: boolean;
  onSaved?: (entry: any) => void;
}) => {
  const { hasPermission } = usePermissions();
  const { toast } = useToastNavigate();
  const [saved, setSaved] = useState({
    latitude: Number(responseApi?.latitude),
    longitude: Number(responseApi?.longitude),
    address: responseApi?.address as string | null,
  });
  const [editing, setEditing] = useState(startEditing);
  const [draft, setDraft] = useState<LatLng | null>(
    startEditing
      ? { lat: Number(responseApi?.latitude), lng: Number(responseApi?.longitude) }
      : null
  );
  const [justification, setJustification] = useState("");
  const [saving, setSaving] = useState(false);

  const lat = saved.latitude;
  const lng = saved.longitude;
  const hasCoords = !Number.isNaN(lat) && !Number.isNaN(lng);
  const canEdit = hasPermission("edit-time-entry-location");

  const cancelEdit = () => {
    setEditing(false);
    setDraft(null);
    setJustification("");
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    const { data, ok } = await updateTimeEntryLocation(responseApi.uuid, {
      latitude: draft.lat,
      longitude: draft.lng,
      justification: justification.trim(),
    });
    setSaving(false);
    if (!ok) return;

    setSaved({
      latitude: Number(data?.response?.latitude),
      longitude: Number(data?.response?.longitude),
      address: data?.response?.address ?? null,
    });
    cancelEdit();
    onSaved?.(data?.response);
    toast({ description: data?.message || "Local da batida atualizado." });
  };

  const body = (
    (
          <div className="flex flex-col gap-4">
            <div className="bg-primary text-primary-foreground px-4 py-3 rounded-md">
              <span className="font-semibold">
                Colaborador: {responseApi?.employee?.user?.name}
                {responseApi?.employee?.position?.name
                  ? ` - ${responseApi.employee.position.name}`
                  : ""}
              </span>
            </div>

            {(responseApi?.is_late || responseApi?.within_geofence === false) && (
              <div className="flex flex-col gap-1 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                {responseApi?.is_late && (
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="size-4 shrink-0" />
                    Batida registrada com atraso.
                  </div>
                )}
                {responseApi?.within_geofence === false && (
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="size-4 shrink-0" />
                    Batida registrada fora da cerca virtual da unidade.
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-md border p-3 bg-zinc-50 dark:bg-[#2c2c2c]">
                <div className="text-xs text-muted-foreground">
                  Horário da Batida
                </div>
                <div className="font-semibold">
                  {responseApi?.clocked_at
                    ? new Date(responseApi.clocked_at).toLocaleString("pt-BR")
                    : "-"}
                </div>
              </div>
              <div className="rounded-md border p-3 bg-zinc-50 dark:bg-[#2c2c2c]">
                <div className="text-xs text-muted-foreground">Tipo</div>
                <div className="font-semibold">
                  <TimeEntryTypeBadge type={responseApi?.time_entry_type_id} />
                </div>
              </div>
              <div className="rounded-md border p-3 bg-zinc-50 dark:bg-[#2c2c2c]">
                <div className="text-xs text-muted-foreground">Setor</div>
                <div className="font-semibold">
                  {responseApi?.employee?.sector?.name || "Não informado"}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[1fr_260px] gap-4">
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between gap-2">
                  <label className="text-sm font-medium">Localização</label>
                  {canEdit && hasCoords && !editing && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 gap-1"
                      onClick={() => {
                        setDraft({ lat, lng });
                        setEditing(true);
                      }}
                    >
                      <Pencil className="size-3.5" /> Editar local
                    </Button>
                  )}
                </div>
                <div className="h-[320px] w-full overflow-hidden rounded-md border">
                  {hasCoords ? (
                    <TimeEntryMap
                      position={{ lat, lng }}
                      address={saved.address ?? undefined}
                      editing={editing}
                      onChange={setDraft}
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                      Sem coordenadas registradas
                    </div>
                  )}
                </div>
                {editing && (
                  <div className="flex flex-col gap-2 rounded-md border bg-muted/40 p-3">
                    <p className="text-xs text-muted-foreground">
                      Arraste o marcador (ou clique no mapa) até o endereço correto.
                      {draft
                        ? ` Novo ponto: ${draft.lat.toFixed(6)}, ${draft.lng.toFixed(6)}`
                        : ""}
                    </p>
                    <Textarea
                      value={justification}
                      onChange={(e) => setJustification(e.target.value)}
                      placeholder="Justificativa da correção (mínimo 10 caracteres)"
                      rows={2}
                    />
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" size="sm" onClick={cancelEdit} disabled={saving}>
                        Cancelar
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        onClick={save}
                        disabled={saving || justification.trim().length < 10}
                      >
                        {saving ? "Salvando..." : "Salvar local"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-3">
                <label className="text-sm font-medium flex items-center gap-1">
                  <Smartphone className="size-4" /> Informações
                </label>
                <img
                  src={responseApi?.photo_path || LogoFavicon}
                  alt="Foto da batida"
                  className="w-full max-w-[200px] aspect-square object-cover rounded-md border"
                />
                <div>
                  <div className="text-xs text-muted-foreground">
                    Versão do App
                  </div>
                  <div className="text-sm">
                    {responseApi?.app_version || "-"}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">
                    Dispositivo
                  </div>
                  <div className="text-sm capitalize">
                    {responseApi?.device || "-"}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">IP</div>
                  <div className="text-sm">
                    {responseApi?.ip_address || "-"}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    <MapPin className="size-3" /> Localização
                  </div>
                  <div className="text-sm">
                    {saved.address ||
                      (hasCoords ? `${lat}, ${lng}` : "Não informado")}
                  </div>
                </div>
                {responseApi?.justification && (
                  <div>
                    <div className="text-xs text-muted-foreground">
                      Justificativa (ajuste manual)
                    </div>
                    <div className="text-sm">{responseApi.justification}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
  )
  );
  return body;
};

export const ShowTimeEntryBtn = ({
  uuid,
  onSaved,
}: {
  uuid: string;
  onSaved?: (entry: any) => void;
}) => {
  return (
    <ShowBtn
      labels={{ title: "Detalhes da Batida" }}
      className="sm:max-w-[900px]"
      params={{
        func: getShowTimeEntry,
        funcParams: uuid,
      }}
      render={(responseApi: any) => (
        <TimeEntryDetails responseApi={responseApi} onSaved={onSaved} />
      )}
      loader={<CreateLoaderInput lines={6} />}
    />
  );
};

// Atalho da lista para batidas fora da cerca: abre os detalhes já no modo de
// edição, com o marcador pronto pra arrastar até o endereço correto.
export const EditTimeEntryLocationBtn = ({
  uuid,
  onSaved,
}: {
  uuid: string;
  onSaved?: (entry: any) => void;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        title="Corrigir local no mapa"
        className="size-7 p-0 text-amber-600 hover:bg-stone-950/10"
        onClick={() => setOpen(true)}
      >
        <MapPinned className="size-5" />
      </Button>
      <ShowBtn
        hideTrigger
        open={open}
        onOpenChange={setOpen}
        labels={{ title: "Corrigir local da batida" }}
        className="sm:max-w-[900px]"
        params={{ func: getShowTimeEntry, funcParams: uuid }}
        render={(responseApi: any) => (
          <TimeEntryDetails
            responseApi={responseApi}
            startEditing
            onSaved={(entry) => {
              onSaved?.(entry);
              setOpen(false);
            }}
          />
        )}
        loader={<CreateLoaderInput lines={6} />}
      />
    </>
  );
};
